import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Global } from '@emotion/react';
import {
  Box,
  Button,
  Flex,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  useColorModeValue
} from '@chakra-ui/react';
import Tree from 'react-d3-tree';
import HelpModalVis from './HelpModalVis';

/* Pomocné funkcie */
function pruneTree(node, currentStep) {
  if (!node || node.creationStep > currentStep) return null;
  const newNode = { ...node, children: [] };
  if (node.children?.length) {
    newNode.children = node.children
      .map(child => pruneTree(child, currentStep))
      .filter(Boolean);
  }
  return newNode;
}

function markActivePath(node, targetId) {
  if (!node) return false;
  const selfHit = node.id === targetId;
  const childHit = node.children?.some(child => markActivePath(child, targetId));
  node.active = selfHit || childHit;
  return node.active;
}

function getDecisionColor(decision = '', def = 'black') {
  if (decision.includes('Conflict'))   return 'orange.600';
  if (decision.includes('Assign'))     return 'green.600';
  if (decision.includes('Backtrack'))  return 'red.600';
  if (decision.includes('Unit prop'))  return 'blue.600';
  return def;
}

function getAssignmentUpToStep(steps = [], stepIdx) {
  const assignment = {};
  if (!Array.isArray(steps)) return assignment;
  const last = Math.min(stepIdx, steps.length - 1);
  for (let i = 0; i <= last; i++) {
    const step = steps[i];
    const m = step.decision?.match(/([a-zA-Z0-9_]+)=(true|false)/);
    if (m) {
      assignment[m[1]] = m[2] === 'true';
    }
    step.unitProps?.forEach(str => {
      const [v, val] = str.split('=');
      assignment[v] = val === 'true';
    });
  }
  return assignment;
}

/* Komponent */
export default function DPLLVisualization({
  steps,
  isSat,
  treeData,
  onStepChange = () => {},
}) {
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const treeContainerRef = useRef(null);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  // theme‐aware colors
  const bgPanel       = useColorModeValue('gray.100', 'gray.700');
  const bgContainer   = useColorModeValue('white',     'gray.800');
  const textColor     = useColorModeValue('black',     'white');
  const nodeTextColor = useColorModeValue('#1A202C',   '#E2E8F0');
  const defaultDecisionColor = useColorModeValue('black', 'white');
  // the only new line: choose link color by theme
  const linkStroke    = useColorModeValue('#1A202C',   '#FFFFFF');

  useEffect(() => setCurrentStepIdx(0), [steps]);
  useEffect(() => onStepChange(currentStepIdx), [currentStepIdx, onStepChange]);

  let partialTreeData = null;
  if (treeData) {
    partialTreeData = pruneTree(treeData, currentStepIdx);
    markActivePath(partialTreeData, `step-${currentStepIdx}`);
  }

  useEffect(() => {
    if (treeContainerRef.current && partialTreeData) {
      const { clientWidth, clientHeight } = treeContainerRef.current;
      setTranslate({ x: clientWidth / 2, y: clientHeight / 3 });
    }
  }, [partialTreeData]);

  const renderNode = useCallback(
    ({ nodeDatum, toggleNode }) => {
      const fill = nodeDatum.attributes?.conflict
        ? '#E53E3E'
        : nodeDatum.attributes?.satLeaf
          ? '#2F855A'
          : nodeDatum.active
            ? '#3182CE'
            : '#FFFFFF';
      return (
        <g onClick={toggleNode} style={{ cursor: 'pointer' }}>
          <circle r={10} fill={fill} stroke="#1A202C" strokeWidth={1.2} />
          <text
            x={15}
            y={4}
            fontSize="0.8rem"
            fontWeight={nodeDatum.active ? 700 : 500}
            fill={nodeTextColor}
            stroke="none"
          >
            {nodeDatum.name}
          </text>
        </g>
      );
    },
    [nodeTextColor]
  );

  const stats = useMemo(() => {
    if (!steps) return { assigns: 0, conflicts: 0, backtracks: 0, unitProps: 0 };
    return steps.reduce((acc, s) => {
      if (s.decision.includes('Assign'))    acc.assigns++;
      if (s.decision.includes('Conflict'))  acc.conflicts++;
      if (s.backtrack)                      acc.backtracks++;
      if (s.decision.includes('Unit prop')) acc.unitProps++;
      return acc;
    }, { assigns: 0, conflicts: 0, backtracks: 0, unitProps: 0 });
  }, [steps]);

  const total   = steps?.length ?? 0;
  const current = steps?.[currentStepIdx];
  const prev    = () => setCurrentStepIdx(i => Math.max(i - 1, 0));
  const next    = () => setCurrentStepIdx(i => Math.min(i + 1, total - 1));
  const showAll = () => setCurrentStepIdx(total - 1);

  const currentAssignment = getAssignmentUpToStep(steps, currentStepIdx);
  const sortedVars = Object.keys(currentAssignment).sort();

  return (
    <>
      {/* Global override so that every <path class="link"> uses our theme‐aware color */}
      <Global
        styles={`
          path.link {
            stroke: ${linkStroke} !important;
            stroke-width: 1.5px !important;
          }
        `}
      />

      <Box bg={bgContainer} p={4} boxShadow="md" color={textColor}>
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontWeight="semibold" fontSize="lg">Vizualizácia DPLL</Text>
          <HelpModalVis />
        </Flex>

        <Flex gap={2} mb={3}>
          <Button onClick={prev} isDisabled={!steps || currentStepIdx === 0}>Prev step</Button>
          <Button onClick={next} isDisabled={!steps || currentStepIdx === total - 1}>Next step</Button>
          <Button onClick={showAll} variant="outline" isDisabled={!steps || total === 0 || currentStepIdx === total - 1}>
            Show all
          </Button>
        </Flex>

        {current && (
          <Box mb={4} p={2} bg={bgPanel} borderRadius="md">
            <Text>Krok: {currentStepIdx + 1} / {total}</Text>
            <Text
              color={getDecisionColor(current.decision, defaultDecisionColor)}
              fontWeight="semibold"
            >
              {current.decision}
            </Text>
            {current.backtrack && (
              <Text color="red.600" fontWeight="bold">Backtracking!</Text>
            )}
          </Box>
        )}

        <Box ref={treeContainerRef} w="100%" h="400px" position="relative" mb={4}>
          {partialTreeData ? (
            <Tree
              data={partialTreeData}
              orientation="vertical"
              translate={translate}
              renderCustomNodeElement={renderNode}
              transitionDuration={400}
              pathFunc="diagonal"
            />
          ) : (
            <Text p={4} color="gray.500">(Žiadne dáta pre strom)</Text>
          )}
        </Box>

        {sortedVars.length > 0 && (
          <Box mt={6} p={3} bg={bgPanel} borderRadius="md">
            <Text fontWeight="semibold" mb={2}>Aktuálne priradenie premenných:</Text>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Premenná</Th>
                  <Th>Hodnota</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedVars.map(v => (
                  <Tr key={v}>
                    <Td>{v}</Td>
                    <Td color={currentAssignment[v] ? 'green.600' : 'red.600'} fontWeight="bold">
                      {currentAssignment[v] ? 'true' : 'false'}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>
    </>
  );
}
